import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, useRef, Suspense, lazy } from "react";
import { toast } from "sonner";
import { CheckCircle, CalendarBlank, CrownSimple } from "@phosphor-icons/react";
import { TRIAL_DAYS } from "@/lib/firestore";
import { SplashScreen, LoadingScreen } from "@/components/SplashScreen";
import ForcePasswordChange from "@/components/ForcePasswordChange";

// Route-level code splitting — each of these ships as its own chunk instead
// of all being bundled into the single initial download. SplashScreen stays
// a normal import since it's also used as the Suspense fallback below (it
// must render instantly, not wait on its own chunk).
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const CoachDashboard = lazy(() => import("@/pages/CoachDashboard"));
const ClientDashboard = lazy(() => import("@/pages/ClientDashboard"));
const SubscriptionPage = lazy(() => import("@/pages/SubscriptionPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const BusinessDashboard = lazy(() => import("@/pages/BusinessDashboard"));

// Payment confirmation modal shown after Cashfree redirect
function PaymentConfirmedModal({ plan, onClose }) {
  const MONTHLY_PRICE = 599;
  const YEARLY_PRICE = 5999;
  const price = plan === "yearly" ? YEARLY_PRICE : MONTHLY_PRICE;
  const period = plan === "yearly" ? "year" : "month";

  // First charge date = today + TRIAL_DAYS
  const firstChargeDate = new Date();
  firstChargeDate.setDate(firstChargeDate.getDate() + TRIAL_DAYS);
  const formattedDate = firstChargeDate.toLocaleDateString("en-IN", {
    day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-3xl p-7 max-w-sm w-full shadow-2xl text-center">

        {/* Success icon */}
        <div className="w-16 h-16 rounded-full bg-[var(--brand)]/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={36} weight="fill" className="text-[var(--brand)]" />
        </div>

        {/* Headline */}
        <div className="flex items-center justify-center gap-2 mb-1">
          <CrownSimple size={16} weight="fill" className="text-amber-400" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-bold">Pro Plan Activated</span>
        </div>
        <h2 className="font-display text-3xl tracking-tight text-[#1a2b3b] leading-tight mb-2">
          YOU'RE ALL SET!
        </h2>
        <p className="text-sm text-neutral-500 mb-6 leading-relaxed">
          The ₹2 verification charge has been refunded. Your {TRIAL_DAYS}-day free trial has started.
        </p>

        {/* Billing summary */}
        <div className="bg-neutral-50 border border-black/5 rounded-2xl p-4 mb-6 text-left flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <CalendarBlank size={18} weight="duotone" className="text-[var(--brand)] mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-bold mb-0.5">
                First charge
              </div>
              <div className="text-sm font-semibold text-[#1a2b3b]">
                {formattedDate}
              </div>
              <div className="text-xs text-neutral-400 mt-0.5">
                After your {TRIAL_DAYS}-day free trial ends
              </div>
            </div>
          </div>
          <div className="border-t border-black/5 pt-3 flex items-start gap-3">
            <CrownSimple size={18} weight="duotone" className="text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-bold mb-0.5">
                Auto-debit every {period}
              </div>
              <div className="text-sm font-semibold text-[#1a2b3b]">
                ₹{price.toLocaleString("en-IN")} / {period}
              </div>
              <div className="text-xs text-neutral-400 mt-0.5">
                Cancel anytime before {formattedDate} to avoid any charge
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full h-12 rounded-xl bg-[var(--brand)] text-white font-bold text-sm tracking-wider hover:bg-[var(--brand)]/90 transition-colors"
        >
          START COACHING →
        </button>
      </div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="min-h-dvh bg-white flex flex-col items-center justify-center px-6 text-center">
      <div style={{ fontFamily: "Inter, Arial, sans-serif", fontSize: "36px", fontWeight: "800", color: "#10B981", letterSpacing: "0.5px", marginBottom: "32px" }}>
        seshly.
      </div>
      <div className="max-w-xs">
        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-bold mb-3">404</div>
        <h2 className="text-2xl font-bold text-[#1a2b3b] mb-3">Page not found</h2>
        <p className="text-sm text-neutral-500 mb-6 leading-relaxed">
          This page doesn't exist. You may have mistyped the address.
        </p>
        <button
          onClick={() => window.location.replace("/")}
          className="h-11 px-6 rounded-xl bg-[var(--brand)] text-white font-bold text-sm tracking-wider hover:bg-[var(--brand)]/90 transition-colors"
        >
          Go to home
        </button>
      </div>
    </div>
  );
}

function Gate() {
  const { user, profile, loading, signOut, needsOAuthSetup } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showBizDash, setShowBizDash] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [subConfirmed, setSubConfirmed] = useState(null); // "monthly" | "yearly" | null
  // Track whether we have ever completed the first load
  const firstLoadRef = useRef(false);

  useEffect(() => {
    // Only run the splash timer once — on the very first time
    // Firebase resolves (loading goes false for the first time)
    if (!loading && !firstLoadRef.current) {
      firstLoadRef.current = true;
      // Allow the splash fly-in animation to finish before dismissing
      const timer = setTimeout(() => setInitialLoadDone(true), 1600);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed") === "true") {
      const plan = params.get("plan") || "monthly";
      setSubConfirmed(plan);
      window.history.replaceState({}, "", "/");
    }
  }, []);

  // Unknown URL path → 404 (app only lives at "/")
  if (window.location.pathname !== "/") return <NotFoundPage />;

  // Privacy and Terms accessible from anywhere
  if (showPrivacy) return <PrivacyPage onBack={() => setShowPrivacy(false)} />;
  if (showTerms) return <TermsPage onBack={() => setShowTerms(false)} />;

  // Show animated splash only on very first app load
  // After sign out, firstLoadRef.current is true so this is skipped
  if (loading || !initialLoadDone) return <SplashScreen />;

  // No user OR Google OAuth complete but profile not yet created → show auth
  if (!user || needsOAuthSetup) {
    return (
      <AuthPage
        onShowPrivacy={() => setShowPrivacy(true)}
        onShowTerms={() => setShowTerms(true)}
      />
    );
  }

  // User exists but profile still fetching — branded screen not white
  if (!profile) return <LoadingScreen />;

  // Admin-suspended coach — block platform access with a clear message
  if (profile.role === "coach" && profile.userStatus === "suspended") {
    return (
      <div className="min-h-dvh bg-white flex flex-col items-center justify-center px-6 text-center">
        <div style={{ fontFamily: "Inter, Arial, sans-serif", fontSize: "36px", fontWeight: "800", color: "#10B981", letterSpacing: "0.5px", marginBottom: "24px" }}>
          seshly.
        </div>
        <div className="max-w-xs">
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-bold mb-3">Account Suspended</div>
          <h2 className="text-2xl font-bold text-[#1a2b3b] mb-3">Your account has been suspended.</h2>
          <p className="text-sm text-neutral-500 mb-6 leading-relaxed">
            Please contact your admin for support.
          </p>
          <button
            onClick={signOut}
            className="text-sm text-neutral-400 underline underline-offset-2 hover:text-neutral-600"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Coach routing
  if (profile.role === "coach") {
    if (showUpgrade) {
      return (
        <SubscriptionPage
          onBack={() => setShowUpgrade(false)}
          onShowPrivacy={() => setShowPrivacy(true)}
          onShowTerms={() => setShowTerms(true)}
        />
      );
    }
    if (showBizDash) {
      return (
        <BusinessDashboard
          onBack={() => setShowBizDash(false)}
          onShowPrivacy={() => setShowPrivacy(true)}
          onShowTerms={() => setShowTerms(true)}
        />
      );
    }
    return (
      <>
        <CoachDashboard
          onUpgrade={() => setShowUpgrade(true)}
          onShowPrivacy={() => setShowPrivacy(true)}
          onShowTerms={() => setShowTerms(true)}
          onBusinessDashboard={() => setShowBizDash(true)}
        />
        {subConfirmed && (
          <PaymentConfirmedModal plan={subConfirmed} onClose={() => setSubConfirmed(null)} />
        )}
      </>
    );
  }

  // Client with a coach-generated temp password — must set their own first
  if (profile.mustChangePassword) {
    return <ForcePasswordChange />;
  }

  // Client routing
  return (
    <ClientDashboard
      onShowPrivacy={() => setShowPrivacy(true)}
      onShowTerms={() => setShowTerms(true)}
    />
  );
}

// Global keyboard fix — scrolls focused input into view above the keyboard on iOS
function useKeyboardScrollFix() {
  useEffect(() => {
    function onFocusIn(e) {
      const el = e.target;
      if (!el.matches("input, textarea, select")) return;
      // Wait for keyboard animation to finish (~300ms), then center the input
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 320);
    }
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, []);
}

export default function App() {
  useKeyboardScrollFix();
  return (
    <AuthProvider>
      <BrandProvider>
        <Suspense fallback={<SplashScreen />}>
          <Gate />
        </Suspense>
        <Toaster
          theme="dark"
          position="bottom-center"
          duration={2500}
          toastOptions={{
            style: {
              background: "#121212",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#fff",
              borderRadius: 0,
            },
          }}
        />
      </BrandProvider>
    </AuthProvider>
  );
}